import { useEffect } from 'react';
import firebase from 'firebase/app';

/**
 * The customer's own colours (Aaron, 5 Nov 2026): the only part of the look a customer can change.
 *   complete    project frame and strip when a project is complete
 *   incomplete  the same when it is incomplete
 *   bucket      the bar along the top of each bucket on a project (Videos & Audio, Assets, Notes, Gallery)
 * Stored in settings/colours (viewers read, admins write, per the database rules). Applied as CSS
 * variables on the page, which util/ui.ts reads, so every screen updates straight away.
 */
export type ThemeColours = { complete: string; incomplete: string; bucket: string };

export const COLOUR_DEFAULTS: ThemeColours = { complete: '#12B158', incomplete: '#E5056A', bucket: '#9E1269' };

export const COLOUR_LABELS: Record<keyof ThemeColours, { label: string; help: string }> = {
  complete: { label: 'Complete projects', help: 'Frame and top strip of a complete project' },
  incomplete: { label: 'Incomplete projects', help: 'Frame and top strip of an incomplete project' },
  bucket: { label: 'Bucket bars', help: 'The bar along the top of Videos & Audio, Assets, Notes and Gallery' },
};

const HEX = /^#[0-9a-fA-F]{6}$/;
export const cleanColours = (raw: any): ThemeColours => {
  const out = { ...COLOUR_DEFAULTS };
  (Object.keys(COLOUR_DEFAULTS) as (keyof ThemeColours)[]).forEach((k) => {
    const v = raw && typeof raw[k] === 'string' ? raw[k].trim() : '';
    if (HEX.test(v)) out[k] = v.toUpperCase();
  });
  return out;
};

/** Puts colours on the page (also used for the live preview while editing). */
export const applyColours = (c: ThemeColours) => {
  const root = document.documentElement.style;
  root.setProperty('--archy-complete', c.complete);
  root.setProperty('--archy-incomplete', c.incomplete);
  root.setProperty('--archy-bucket', c.bucket);
};

let saved: ThemeColours = COLOUR_DEFAULTS;
export const savedColours = () => saved;

/** Mount once, after sign-in: keeps the page in step with settings/colours. */
const useThemeColours = () => {
  useEffect(() => {
    const unsub = firebase
      .firestore()
      .collection('settings')
      .doc('colours')
      .onSnapshot(
        (d) => {
          saved = cleanColours(d.data());
          applyColours(saved);
        },
        () => undefined,
      );
    return () => unsub();
  }, []);
};

export default useThemeColours;
